/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Check, X, Eye, EyeOff, Shield, AlertTriangle } from 'lucide-react';

// Password Policy (must match backend)
const PASSWORD_POLICY = {
  minLength: 12,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true,
  specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?'
};

// Common passwords to reject
const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password123', '123456', '12345678',
  'qwerty', 'letmein', 'welcome', 'admin', 'login', 'abc123',
  'iloveyou', 'sunshine', 'princess', 'football', 'monkey',
  'shadow', 'dragon', 'master', 'superman', 'batman'
]);

/**
 * Validate password against policy
 */
const validatePassword = (password, options = {}) => {
  const checks = {
    minLength: password.length >= PASSWORD_POLICY.minLength,
    maxLength: password.length <= PASSWORD_POLICY.maxLength,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
    notCommon: !COMMON_PASSWORDS.has(password.toLowerCase()),
    noSequential: !hasSequentialChars(password),
    noRepeated: !hasRepeatedChars(password),
    notContainsUsername: !options.username || !password.toLowerCase().includes(options.username.toLowerCase()),
    notContainsEmail: !options.email || !password.toLowerCase().includes(options.email.split('@')[0].toLowerCase())
  };

  // Calculate strength score (0-4)
  let score = 0;
  if (checks.minLength) score += 1;
  if (checks.hasUppercase && checks.hasLowercase) score += 0.5;
  if (checks.hasNumber) score += 0.5;
  if (checks.hasSpecial) score += 0.5;
  if (checks.notCommon && checks.noSequential && checks.noRepeated) score += 0.5;
  if (password.length >= 16) score += 0.5;
  if (password.length >= 20) score += 0.5;

  const strength = Math.min(4, Math.round(score));
  const isValid = Object.values(checks).every(v => v) && strength >= 3;

  return { checks, strength, isValid };
};

const hasSequentialChars = (password) => {
  const sequences = ['abcdefghijklmnopqrstuvwxyz', 'zyxwvutsrqponmlkjihgfedcba', '01234567890', '09876543210', 'qwertyuiop'];
  const lower = password.toLowerCase();
  return sequences.some(seq => {
    for (let i = 0; i <= seq.length - 4; i++) {
      if (lower.includes(seq.substring(i, i + 4))) return true;
    }
    return false;
  });
};

const hasRepeatedChars = (password) => /(.)\1{3,}/.test(password);

/**
 * Password Strength Meter Component
 */
export const PasswordStrengthMeter = ({ strength }) => {
  const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500'];
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[0, 1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
              level <= strength ? colors[strength] : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <div className="flex justify-between items-center">
        <span className={`text-xs font-medium ${
          strength <= 1 ? 'text-red-600' : 
          strength === 2 ? 'text-yellow-600' : 
          'text-green-600'
        }`}>
          {labels[strength]}
        </span>
        {strength >= 3 && (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <Shield className="w-3 h-3" /> Meets requirements
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Password Requirements Checklist
 */
export const PasswordRequirements = ({ checks, showAll = false }) => {
  const requirements = [
    { key: 'minLength', label: 'At least 12 characters', required: true },
    { key: 'hasUppercase', label: 'One uppercase letter (A-Z)', required: true },
    { key: 'hasLowercase', label: 'One lowercase letter (a-z)', required: true },
    { key: 'hasNumber', label: 'One number (0-9)', required: true },
    { key: 'hasSpecial', label: 'One special character (!@#$...)', required: true },
    { key: 'notCommon', label: 'Not a common password', required: true },
    { key: 'noSequential', label: 'No sequential characters', required: false },
    { key: 'noRepeated', label: 'No repeated characters (aaa)', required: false }
  ];

  const visibleRequirements = showAll 
    ? requirements 
    : requirements.filter(r => r.required || !checks[r.key]);

  return (
    <div className="mt-3 space-y-1">
      <p className="text-xs font-medium text-gray-600 mb-2">Password Requirements:</p>
      {visibleRequirements.map(({ key, label, required }) => (
        <div key={key} className="flex items-center gap-2 text-xs">
          {checks[key] ? (
            <Check className="w-3.5 h-3.5 text-green-500" />
          ) : (
            <X className={`w-3.5 h-3.5 ${required ? 'text-red-500' : 'text-gray-400'}`} />
          )}
          <span className={checks[key] ? 'text-green-700' : required ? 'text-gray-700' : 'text-gray-500'}>
            {label}
          </span>
        </div>
      ))}
    </div>
  );
};

/**
 * Password Input with Strength Indicator
 */
const PasswordInput = ({
  value,
  onChange,
  onValidationChange,
  placeholder = 'Enter password',
  label = 'Password',
  username = '',
  email = '',
  showRequirements = true,
  showMeter = true,
  disabled = false,
  error = '',
  className = ''
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const validation = useMemo(() => {
    if (!value) return { checks: {}, strength: 0, isValid: false };
    return validatePassword(value, { username, email });
  }, [value, username, email]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validation);
    }
  }, [validation, onValidationChange]);

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-2.5 pr-12 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
            error ? 'border-red-500' : 
            validation.isValid && value ? 'border-green-500' : 
            'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {error}
        </p>
      )}

      {value && showMeter && (
        <PasswordStrengthMeter strength={validation.strength} />
      )}

      {value && showRequirements && (isFocused || !validation.isValid) && (
        <PasswordRequirements checks={validation.checks} showAll={isFocused} />
      )}
    </div>
  );
};

/**
 * Confirm Password Input
 */
export const ConfirmPasswordInput = ({
  password,
  confirmPassword,
  onChange,
  label = 'Confirm Password',
  placeholder = 'Confirm your password',
  disabled = false,
  className = ''
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const matches = password && confirmPassword && password === confirmPassword;
  const showError = confirmPassword && !matches;

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-2.5 pr-12 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors ${
            showError ? 'border-red-500' : 
            matches ? 'border-green-500' : 
            'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>

      {showError && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <X className="w-3 h-3" /> Passwords do not match
        </p>
      )}

      {matches && (
        <p className="text-xs text-green-600 flex items-center gap-1">
          <Check className="w-3 h-3" /> Passwords match
        </p>
      )}
    </div>
  );
};

/**
 * Password Change Form Component
 */
export const PasswordChangeForm = ({
  onSubmit,
  loading = false,
  requireCurrentPassword = true,
  username = '',
  email = ''
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validation, setValidation] = useState({ isValid: false });
  const [error, setError] = useState('');

  const canSubmit = (
    (!requireCurrentPassword || currentPassword) &&
    validation.isValid &&
    newPassword === confirmPassword &&
    !loading
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!canSubmit) return;

    try {
      await onSubmit({
        currentPassword,
        newPassword
      });
    } catch (err) {
      setError(err.message || 'Failed to change password');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {requireCurrentPassword && (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Current Password
          </label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Enter current password"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            required
          />
        </div>
      )}

      <PasswordInput
        value={newPassword}
        onChange={setNewPassword}
        onValidationChange={setValidation}
        label="New Password"
        placeholder="Enter new password"
        username={username}
        email={email}
      />

      <ConfirmPasswordInput
        password={newPassword}
        confirmPassword={confirmPassword}
        onChange={setConfirmPassword}
      />

      <button
        type="submit"
        disabled={!canSubmit}
        className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
          canSubmit
            ? 'bg-purple-600 text-white hover:bg-purple-700'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        {loading ? 'Changing Password...' : 'Change Password'}
      </button>
    </form>
  );
};

export default PasswordInput;
